/* RESPONSIBLE TEAM: team-automation-foundations */
import BaseValidator from 'ember-cp-validations/validators/base';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Step from 'embercom/models/operator/visual-builder/step';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import Group from 'embercom/models/operator/visual-builder/group';
import Workflow from 'embercom/models/operator/visual-builder/workflow';

export default class WorkflowLocalVariablesExist extends BaseValidator {
  @service declare intl: IntlService;

  validate(
    _value: any,
    _options: any,
    model: Step | ConnectionPoint | Group | Workflow,
  ): string | true {
    let editorConfig = model.editorConfig;
    if (!editorConfig) {
      return true;
    }

    let localVariableIdentifiers = model.localVariableIdentifiersUsed;
    let allLocalVariablesMap = Object.fromEntries(
      editorConfig.localAttributes.map((attribute) => [attribute.identifier, attribute]),
    ) as Record<string, AttributeDescriptor>;

    let invalidVariables = localVariableIdentifiers.filter(
      (identifier) => !allLocalVariablesMap[identifier],
    );

    // ignore checking if model's workflow is not current editor workflow
    // the editor workflow could be a draft workflow, the attributes used in the draft workflow and workflow could be different
    // ideally we should have a separate editor config for each workflow, we should refactor this later.
    let editorWorkflow = editorConfig.workflow;
    let modelWorkflow = this.getModelWorkflow(model);

    if (modelWorkflow?.id !== editorWorkflow?.id) {
      return true;
    }

    if (invalidVariables.length > 0) {
      return this.intl.t(
        'operator.workflows.visual-builder.validations.invalid-local-variables-in-use',
      );
    }

    return true;
  }

  static getDependentsFor() {
    return ['model.localVariableIdentifiersUsed', 'model.editorConfig.localAttributes'];
  }

  private getModelWorkflow(model: Step | ConnectionPoint | Group | Workflow): Workflow | undefined {
    if (model instanceof Workflow) {
      return model;
    } else if (model instanceof Group) {
      return model.workflow;
    } else if (model instanceof Step) {
      return model.group.workflow;
    } else if (model instanceof ConnectionPoint) {
      return model.group?.workflow || model.step?.group?.workflow;
    } else {
      return undefined;
    }
  }
}
